import { add, isValid, parseISO } from 'date-fns';
import { isEmpty, last } from 'lodash';

import { MODE_TRANSFER, SHIPMENT_STATUS } from '@/lib/constants';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { TimelineItemModel, TimelineSegment } from '@/lib/models/timeline-item/types';
import { SegmentModeType } from '@/types/api-types';

const DISCHARGE_BUFFER_DAYS = 1;

export const transformShipmentsToTimelineItems = (shipments: ShipmentModel[]): TimelineItemModel[] => {
  return shipments.map((shipment) => {
    const completedMilestones = (shipment.milestones || []).filter((milestone) => milestone.completed);
    const segments = buildShipmentTimelineSegments(shipment);
    const lastSegmentIsTransit = last(segments)?.mode !== MODE_TRANSFER;
    const startDate = getShipmentTimelineStartDate(shipment);
    const endDate = getShipmentTimelineEndDate(shipment, lastSegmentIsTransit);

    return {
      startDate: isValid(startDate) ? startDate : '',
      endDate: isValid(endDate) ? endDate : '',
      latestMilestone: shipment.status !== SHIPMENT_STATUS.COMPLETED ? last(completedMilestones)?.id || '' : '',
      segments,
      milestones: shipment.milestones || [],
      status: shipment.status || SHIPMENT_STATUS.PENDING,
      originalShipment: shipment,
    };
  });
};

const getShipmentTimelineStartDate = (shipment: ShipmentModel): Date => {
  const departureDate = parseISO(shipment.atd || shipment.etd || '');
  const firstMilestone = shipment.milestones?.[0] || undefined;
  if (firstMilestone) {
    return new Date(firstMilestone.date);
  }
  return departureDate;
};

const getShipmentTimelineEndDate = (shipment: ShipmentModel, endsInTransit: boolean): Date => {
  let arriveDate = parseISO(shipment.ata || shipment.eta || '');
  const lastMilestone = last(shipment.milestones);
  if (lastMilestone) {
    arriveDate = new Date(lastMilestone.date);
  }

  // If ends in a transit, add an X day buffer
  return endsInTransit ? add(arriveDate, { days: DISCHARGE_BUFFER_DAYS }) : arriveDate;
};

const transferMilestones = [
  'gateOutWithContainerEmpty',
  'gateInWithContainerFull',
  'dischargeFromVessel',
  'gateOutWithContainerFull',
  'gateInWithContainerEmpty',
  'ARR',
  'RCF',
  'NFD',
  'DLV',
];

const transitMilestones = ['loadOnVessel', 'vesselDepartureWithContainerAIS', 'vesselDepartureWithContainer', 'DEP'];

export const buildShipmentTimelineSegments = (shipment: ShipmentModel): TimelineSegment[] => {
  if (!shipment.milestones || isEmpty(shipment.milestones)) return [];

  const { milestones } = shipment;
  const segments: TimelineSegment[] = [];
  const firstMilestone = milestones[0];
  let currentSegment = MODE_TRANSFER; // Initial phase is Transfer

  milestones.forEach((milestone, index) => {
    const { locationCode, date, milestoneCode } = milestone;
    const isLastMilestone = index === milestones.length - 1;
    const prevSegment = last(segments);
    const prevDate = prevSegment ? prevSegment.toDate : new Date(firstMilestone.date);
    const toDate = new Date(date);

    if (transferMilestones.includes(milestoneCode)) {
      if (currentSegment !== MODE_TRANSFER) {
        // Close the current Transit segment
        segments.push(createTransitSegment(locationCode, prevDate, toDate));
        currentSegment = MODE_TRANSFER;
      }
    } else if (transitMilestones.includes(milestoneCode)) {
      if (currentSegment !== 'transit') {
        // Close the current Transfer segment
        segments.push(createTransferSegment(locationCode, prevDate, toDate));
        currentSegment = 'transit';
      }
    } else if (isLastMilestone) {
      // Close the current/last segment
      if (currentSegment === 'transit') {
        segments.push(createTransitSegment(locationCode, prevDate, toDate));
      } else {
        segments.push(createTransferSegment(locationCode, prevDate, toDate));
      }
    }
  });

  return segments;
};

const createTransferSegment = (locationCode: string, from: Date, to: Date): TimelineSegment => {
  return {
    id: `transfer_${locationCode}`,
    fromDate: from,
    toDate: to,
    mode: MODE_TRANSFER as SegmentModeType,
  };
};

const createTransitSegment = (locationCode: string, from: Date, to: Date): TimelineSegment => {
  return {
    id: `transit_${locationCode}`,
    fromDate: from,
    toDate: to,
    mode: 'transit',
  };
};
